import { EnumStorageKeys } from '@/data/protocols/cache/keys'
import PathConstants from '@/main/routes/paths'
import { settings } from '@/main/config/settings'
import { clearSessionId } from '@/data/services/login/session-id-store'
import { markNavigatingAway } from '@/infra/http/navigation-state'

/**
 * A ponte do monólito para o fluxo de autenticação de integradores (authorization code): transforma a
 * sessão recém-criada em código de autorização. É o ÚNICO destino aceito na origem do monólito.
 *
 * Acoplado à rota do monólito (`IdpAuthorizeBridgeController.PATH`, skill-core-platform-svc#145): se ela
 * mudar lá, muda aqui.
 */
export const IDP_BRIDGE_PATH = '/integration/idp/authorize'

const isWebProtocol = (protocol: string): boolean => protocol === 'http:' || protocol === 'https:'

/**
 * Minúsculas e sem barras finais. As barras saem num laço, não por regex: `/\/+$/` retrocede a cada
 * posição e custa O(n²) num caminho com muitas barras que não terminam a string — e o caminho vem do
 * `redirect_uri` da URL, ou seja, de quem montou o link.
 */
const normalizePath = (path: string): string => {
  let end = path.length
  while (end > 0 && path[end - 1] === '/') {
    end--
  }
  return path.slice(0, end).toLowerCase() || '/'
}

/**
 * A origem do monólito, lida de `SKILLWEB_HOST`, ou `null` quando a configuração não a define.
 * `SKILLWEB_HOST` malformado ou ausente não derruba a validação: a origem própria continua valendo.
 */
const monolithOrigin = (): string | null => {
  try {
    if (!settings.SKILLWEB_HOST) {
      return null
    }
    const monolith = new URL(settings.SKILLWEB_HOST)
    // Um host sem scheme ("app.skillhub.me:443", "localhost:8080") não lança: o parser lê o host
    // como scheme e a origem sai "null" — a mesma de `javascript:` e `data:`. Aceitá-la abriria a
    // validação para esses destinos, então só vale uma origem http(s) de verdade.
    return isWebProtocol(monolith.protocol) && monolith.origin !== 'null' ? monolith.origin : null
  } catch {
    // Configuração inválida não pode transformar todo redirect em recusa.
    return null
  }
}

/**
 * Se é seguro continuar para `url` depois do login.
 *
 * - Na origem do próprio portal: qualquer caminho, menos o `/login` (laço).
 * - Na origem do monólito: SÓ a ponte ([IDP_BRIDGE_PATH]). Sem esta entrada o `redirect_uri` do fluxo
 *   de integradores seria descartado, a pessoa cairia na home e o integrador nunca receberia o código.
 *   Qualquer outro caminho do monólito é recusado (PR #37 review): alguns emitem credencial e
 *   redirecionam adiante, e nenhum deles é destino deste fluxo.
 * - Qualquer terceira origem é recusada, que é o que impede redirecionamento aberto.
 */
export const isSafeRedirectUrl = (url: string): boolean => {
  let parsed: URL
  try {
    // Resolve tanto caminhos relativos ("/foo") quanto absolutos contra a origin atual.
    parsed = new URL(url, window.location.origin)
  } catch {
    return false
  }

  // Só navegação web: `javascript:`, `data:` e schemes custom nunca são destino, seja qual for a origem.
  // Pathnames que começam com "//" também não: é como um open redirect costuma entrar.
  if (!isWebProtocol(parsed.protocol) || parsed.pathname.startsWith('//')) {
    return false
  }

  if (parsed.origin === window.location.origin) {
    return normalizePath(parsed.pathname) !== normalizePath(PathConstants.Routes.LOGIN.value)
  }

  if (parsed.origin === monolithOrigin()) {
    return normalizePath(parsed.pathname) === IDP_BRIDGE_PATH
  }

  return false
}

/**
 * Retorna um redirect_uri seguro ou o fallback.
 */
export const getSafeRedirectUri = (searchParams: URLSearchParams, fallback = ''): string => {
  const raw = searchParams.get('redirect_uri') || ''
  return raw && isSafeRedirectUrl(raw) ? raw : fallback
}

/**
 * O destino a levar para a tela de login, ou `undefined` para um `/login` limpo.
 *
 * Um 401 disparado NA PRÓPRIA tela de login (sessionId salvo, sessão do monólito vencida) chega aqui
 * com `returnUrl = /login?redirect_uri=<destino>`. O `/login` em si é recusado (laço), mas o destino
 * pendente não pode se perder junto (PR #37 review): no fluxo de integradores ele é a ponte, e sem ele
 * a pessoa loga, cai na home e o integrador nunca recebe o código. Ele é validado de novo — nunca
 * repassado cru.
 */
const loginReturnTarget = (returnUrl?: string): string | undefined => {
  if (!returnUrl) {
    return undefined
  }
  if (isSafeRedirectUrl(returnUrl)) {
    return returnUrl
  }
  try {
    const parsed = new URL(returnUrl, window.location.origin)
    const isOwnLogin =
      parsed.origin === window.location.origin &&
      normalizePath(parsed.pathname) === normalizePath(PathConstants.Routes.LOGIN.value)
    const pending = isOwnLogin ? parsed.searchParams.get('redirect_uri') : null
    return pending && isSafeRedirectUrl(pending) ? pending : undefined
  } catch {
    return undefined
  }
}

/**
 * Redireciona o usuário para a tela de login, com um possível redirect_uri.
 * Só anexa um destino seguro — nunca a própria /login, o que evita o loop
 * /login?redirect_uri=/login quando um 401 dispara enquanto já estamos em /login. Nesse caso o
 * redirect_uri que o /login carregava é levado adiante ([loginReturnTarget]). Sem destino seguro,
 * redireciona para /login "limpo".
 */
export const redirectToLogin = (returnUrl?: string): void => {
  clearSessionId()

  const target = loginReturnTarget(returnUrl)
  const loginUrl = target
    ? `${PathConstants.Routes.LOGIN.value}?redirect_uri=${encodeURIComponent(target)}`
    : PathConstants.Routes.LOGIN.value

  // Marca a navegação full-page ANTES do replace: as requests em voo abortadas
  // por ele chegam sem resposta e não devem virar NetworkError/snackbar.
  markNavigatingAway()
  window.location.replace(loginUrl)
}

/**
 * Clears all authentication-related data and cookies from the client.
 * Note: HttpOnly cookies can only be cleared by the server.
 */
export const clearAuthData = (): void => {
  const cookies = document.cookie.split(';')
  const hostname = window.location.hostname
  const expiration = 'expires=Thu, 01 Jan 1970 00:00:00 UTC'

  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim()

    // Clear for current path and domain
    document.cookie = `${cookieName}=; ${expiration}; path=/;`
    document.cookie = `${cookieName}=; ${expiration}; path=/; domain=${hostname};`

    // Clear for parent domain if applicable

    const domainParts = hostname.split('.')

    if (domainParts.length > 2) {
      const parentDomain = domainParts.slice(-2).join('.')
      document.cookie = `${cookieName}=; ${expiration}; path=/; domain=.${parentDomain};`
    }
  })

  sessionStorage.clear()
  localStorage.removeItem(EnumStorageKeys.APP_LANGUAGE)
  clearSessionId()
}
